import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH25_QUESTIONS } from "../data/neetPhysicsCh25Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 25: Wave Optics (NCERT Class 12 Physics).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Huygens' Principle", "Interference & Young's Double Slit", "Diffraction", "Polarization"];

const SECTION_COLORS = {
  "Huygens' Principle":                   "#2563eb",
  "Interference & Young's Double Slit":    "#7c3aed",
  "Diffraction":                          "#dc2626",
  "Polarization":                         "#0d9488",
};

const CONCEPTS = [
  // ── Huygens' Principle ──────────────────────────────────────────────────────
  { section: "Huygens' Principle", title: "Wavefront and secondary wavelets",
    explanation: "A wavefront is the locus of all points in a medium that are vibrating in the same phase at a given instant. Huygens' principle states that every point on a wavefront acts as a fresh source of secondary wavelets, which spread out in all directions with the speed of the wave in that medium. The envelope (common tangent) of all these secondary wavelets, drawn a short time later, gives the new position of the wavefront.",
    example: `Wavefront at t=0: locus of points in same phase
Each point on it emits a secondary wavelet, speed v
New wavefront at t=dt = tangent surface touching
                          all secondary wavelets

Point source, far away  -> wavefronts ~ PLANE
Point source, nearby     -> wavefronts SPHERICAL
Line source              -> wavefronts CYLINDRICAL` },
  { section: "Huygens' Principle", title: "Rays and their relation to the wavefront",
    explanation: "A ray is simply a line drawn perpendicular (normal) to the wavefront, indicating the direction in which the wave (and its energy) travels. For a spherical wavefront the rays are radii diverging from the source; for a plane wavefront the rays are parallel straight lines.",
    example: `Spherical wavefront -> rays are radii, diverging outward
Plane wavefront     -> rays are parallel straight lines

Ray direction = perpendicular to wavefront at every point` },
  { section: "Huygens' Principle", title: "Huygens' construction: reflection of a plane wavefront",
    explanation: "Consider a plane wavefront AB incident on a mirror at angle i. By the time the wavelet from B reaches the mirror at C, the wavelet from A has already traveled the same distance v.t and reached a point A' such that AA' = BC = v.t. The reflected wavefront is A'C. Triangles ABC and A'CA share side AC, and since AA' = BC (both = v.t), the two right triangles are congruent, giving angle of incidence i = angle of reflection r.",
    example: `Let v.t = distance travelled by wavelets from A and B
Triangle ABC and A'CA: AC common, AA' = BC = v.t
=> triangles congruent => angle i = angle r

This is Huygens' geometric proof of the LAW OF REFLECTION` },
  { section: "Huygens' Principle", title: "Huygens' construction: refraction of a plane wavefront",
    explanation: "When a plane wavefront crosses from medium 1 (speed v1) into medium 2 (speed v2), the wavelets on the side that has already entered medium 2 travel a different distance (v2.t) than the wavelets still in medium 1 (v1.t) in the same time t. Geometric analysis of the resulting wavefront tilt gives sin i / sin r = v1 / v2 = n2/n1 (Snell's law), where n = c/v is the refractive index.",
    example: `sin i / sin r = v1 / v2   (Huygens' derivation of Snell's law)
Since n = c/v:  sin i / sin r = n2 / n1

Going air -> glass (denser, v2 < v1):
  sin r < sin i  =>  r < i  => ray bends TOWARD normal` },
  { section: "Huygens' Principle", title: "Wave theory vs. Newton's corpuscular theory",
    explanation: "Newton's corpuscular theory predicted that light should travel FASTER in a denser medium (like glass or water) than in a rarer medium (air) to explain refraction bending toward the normal. Huygens' wave theory correctly predicted the opposite -- light travels SLOWER in a denser medium -- and this was experimentally confirmed (Foucault's experiments), which settled the debate in favour of the wave theory of light.",
    example: `Corpuscular theory (Newton):  v_glass > v_air   (WRONG)
Wave theory (Huygens):        v_glass < v_air   (CORRECT,
                                confirmed experimentally)

Refractive index n = c / v  =>  denser medium has LARGER n,
                                 SMALLER v` },
  { section: "Huygens' Principle", title: "Frequency, wavelength and speed across a boundary",
    explanation: "When light passes from one medium into another, its FREQUENCY stays exactly the same (it is fixed by the source), but its SPEED and WAVELENGTH both change according to the medium's refractive index. Since v = f.lambda, a decrease in speed in a denser medium is accompanied by a proportional decrease in wavelength, while f remains constant.",
    example: `f (frequency)      -> UNCHANGED across a boundary
v (speed) = f x lambda
Denser medium: v decreases => lambda decreases (f fixed)

Example: light of wavelength 600 nm in air (n=1) entering
glass (n=1.5): lambda_glass = 600/1.5 = 400 nm; f unchanged` },

  // ── Interference & Young's Double Slit ──────────────────────────────────────
  { section: "Interference & Young's Double Slit", title: "Superposition principle and interference of light",
    explanation: "When two or more light waves overlap at a point in space, the resultant displacement is the vector sum of the individual displacements (principle of superposition). Interference is the modification in the distribution of light intensity that results from this superposition -- producing regions of increased intensity (constructive interference) and regions of decreased/zero intensity (destructive interference).",
    example: `Resultant displacement y = y1 + y2 (superposition)
Resultant intensity I = I1 + I2 + 2.sqrt(I1.I2).cos(phi)
   where phi = phase difference between the two waves

phi = 0        -> maximum constructive interference
phi = pi (180 deg) -> maximum destructive interference` },
  { section: "Interference & Young's Double Slit", title: "Coherent sources and conditions for sustained interference",
    explanation: "For a stable, observable interference pattern, the two sources must be COHERENT: they must have the same frequency (or wavelength) and maintain a CONSTANT phase difference over time. Two independent sources (even of the same colour) are generally incoherent because their phases fluctuate randomly and independently, so any interference pattern washes out too fast to observe. Coherent sources are typically created by splitting light from a single source into two parts.",
    example: `Conditions for sustained interference:
1) Same frequency / wavelength
2) Constant phase difference (coherence)
3) Comparable amplitudes (for good contrast)
4) Sources close together, screen far away (practical YDSE)` },
  { section: "Interference & Young's Double Slit", title: "Young's double slit experiment (YDSE) — setup",
    explanation: "In YDSE, a monochromatic source illuminates a single narrow slit S, whose diffracted light falls on two closely spaced narrow slits S1 and S2 (separation d), which then act as coherent secondary sources. Light from S1 and S2 overlaps on a distant screen (distance D away, D >> d), producing a pattern of equally spaced alternating bright and dark fringes.",
    example: `Source -> single slit S -> two slits S1, S2 (separation d)
                              -> screen (distance D, D >> d)

S1 and S2 are COHERENT because both are illuminated by the
same wavefront arriving from S` },
  { section: "Interference & Young's Double Slit", title: "Path difference in YDSE",
    explanation: "For a point P on the screen at distance y from the centre, with slit separation d and screen distance D (D >> d, y << D), the path difference between the light reaching P from S1 and S2 is approximately delta = y.d/D. This approximation uses the small-angle assumption valid when the screen is far from the slits.",
    example: `Path difference:  delta = S2P - S1P ~ y.d / D
                    (valid when D >> d and y << D)

This delta determines whether P is a bright fringe (delta=nλ),
a dark fringe (delta=(n+1/2)λ), or in between` },
  { section: "Interference & Young's Double Slit", title: "Conditions for constructive interference (bright fringes)",
    explanation: "A bright fringe (constructive interference / maximum intensity) occurs at points where the path difference between the two waves equals a whole number of wavelengths: delta = n.lambda, where n = 0, +-1, +-2, .... At n=0 we get the CENTRAL bright fringe, exactly midway between the two slits.",
    example: `Bright fringe condition:  delta = n.lambda   (n = 0,1,2,...)

n=0 -> central maximum (delta = 0)
n=1 -> first bright fringe on either side (delta = lambda)
n=2 -> second bright fringe (delta = 2.lambda), etc.` },
  { section: "Interference & Young's Double Slit", title: "Conditions for destructive interference (dark fringes)",
    explanation: "A dark fringe (destructive interference / minimum intensity, ideally zero for equal-amplitude waves) occurs where the path difference is an odd multiple of half a wavelength: delta = (n + 1/2).lambda, where n = 0, +-1, +-2, .... These dark fringes lie exactly midway between two consecutive bright fringes.",
    example: `Dark fringe condition:  delta = (n + 1/2).lambda

n=0 -> first dark fringe, delta = lambda/2
n=1 -> second dark fringe, delta = 3.lambda/2, etc.

Between every pair of adjacent bright fringes lies exactly
one dark fringe (and vice versa)` },
  { section: "Interference & Young's Double Slit", title: "Fringe width formula: β = λD/d",
    explanation: "Fringe width (beta) is the distance between two consecutive bright (or two consecutive dark) fringes on the screen. Combining the path-difference formula delta = y.d/D with the bright-fringe condition delta = n.lambda gives the fringe positions y_n = n.lambda.D/d; subtracting consecutive fringe positions gives a CONSTANT fringe width beta = lambda.D/d for all fringes near the centre.",
    example: `y_n = n.lambda.D/d   (position of n-th bright fringe)
beta = y_(n+1) - y_n = lambda.D/d      (fringe width)

Worked example: lambda = 600 nm, D = 1 m, d = 1 mm
beta = (600x10^-9 x 1) / (1x10^-3) = 6x10^-4 m = 0.6 mm` },
  { section: "Interference & Young's Double Slit", title: "Effect of changing λ, D, d on fringe width",
    explanation: "Since beta = lambda.D/d: increasing the wavelength (e.g. switching from blue to red light) increases beta; increasing the screen distance D increases beta; increasing the slit separation d DECREASES beta. Immersing the entire YDSE setup in a medium of refractive index n replaces lambda with lambda/n (since wavelength shrinks in a denser medium), shrinking beta by the same factor n.",
    example: `beta = lambda.D/d

Double d           -> beta HALVES
Double D           -> beta DOUBLES
Immerse in medium n -> lambda -> lambda/n -> beta -> beta/n

Red light (~700 nm) gives WIDER fringes than blue (~450 nm)` },
  { section: "Interference & Young's Double Slit", title: "Intensity distribution and I_max/I_min ratio",
    explanation: "For two coherent waves of amplitudes a1 and a2, the resultant intensity varies as I = I1 + I2 + 2.sqrt(I1.I2).cos(phi). The maximum intensity (phi=0) is proportional to (a1+a2)^2 and the minimum (phi=180deg) to (a1-a2)^2. So I_max/I_min = [(a1+a2)/(a1-a2)]^2, a ratio NEET frequently tests using given amplitude or intensity ratios.",
    example: `I_max / I_min = [(a1 + a2) / (a1 - a2)]^2

If a1 : a2 = 3 : 1 ->
I_max/I_min = (3+1)^2 / (3-1)^2 = 16/4 = 4 : 1

If sources have equal amplitude (a1=a2): I_min = 0 exactly
(perfectly dark fringes)` },

  // ── Diffraction ──────────────────────────────────────────────────────────────
  { section: "Diffraction", title: "Diffraction of light at a single slit",
    explanation: "Diffraction is the bending/spreading of waves around obstacles or through apertures whose size is comparable to the wavelength. When light passes through a single narrow slit of width 'a', secondary wavelets from different parts of the SAME wavefront (across the slit opening) interfere with each other, producing a characteristic pattern on a distant screen: a bright central band flanked by progressively fainter secondary bright and dark bands.",
    example: `Single slit, width a, illuminated by wavelength lambda
Wavelets from different points WITHIN the slit interfere
  with each other (not from two separate slits)

Noticeable diffraction requires:  a is comparable to lambda
  (a >> lambda -> negligible diffraction, sharp shadow)` },
  { section: "Diffraction", title: "Central maximum and secondary minima/maxima — qualitative picture",
    explanation: "The single-slit diffraction pattern has a broad, intense CENTRAL MAXIMUM at the centre (straight-through direction), flanked symmetrically by much weaker SECONDARY MAXIMA, separated by completely dark SECONDARY MINIMA. Each successive secondary maximum is considerably less intense than the previous one, and the central maximum alone contains most of the total light energy in the pattern.",
    example: `Pattern (intensity vs angle theta), single slit:
... dim - DARK - dim - DARK - [BRIGHT CENTRAL MAX] - DARK - dim - DARK - dim ...

Central max width (angular) = 2x width of each secondary max
Central max intensity >> any single secondary maximum` },
  { section: "Diffraction", title: "Positions of minima: a·sinθ = nλ",
    explanation: "To find the diffraction minima, the slit of width a is divided into pairs of narrow strips. Each strip in the top half is paired with a corresponding strip in the bottom half exactly a/2 apart; when the path difference between such a pair is lambda/2, every pair cancels and total destructive interference (a minimum) results. This condition, generalized, gives a.sin(theta) = n.lambda for n = +-1, +-2, +-3, ... (n=0 is excluded, since that is the central maximum).",
    example: `Minima condition:  a . sin(theta) = n.lambda   (n = ±1,±2,...)

Slit divided into pairs of strips a/2 apart; each pair's path
difference (a/2).sin(theta) = lambda/2 causes cancellation
when a.sin(theta) = lambda (first minimum, n=1), etc.` },
  { section: "Diffraction", title: "Positions of secondary maxima (approximate)",
    explanation: "Between consecutive minima, secondary maxima appear approximately (but not exactly) midway, at roughly a.sin(theta) ~ (n + 1/2).lambda for n = 1, 2, 3, .... These secondary maxima rapidly decrease in intensity as n increases -- the first secondary maximum has only about 4.5% of the intensity of the central maximum, and subsequent ones are fainter still.",
    example: `Secondary maxima (approx.):  a.sin(theta) ~ (n + 1/2).lambda

Relative intensities (central max = 1):
1st secondary max  ~ 0.045  (~4.5%)
2nd secondary max  ~ 0.016  (~1.6%)
-- rapidly decreasing, unlike YDSE's equal-intensity fringes` },
  { section: "Diffraction", title: "Angular and linear width of the central maximum",
    explanation: "The central maximum extends from the first minimum on one side (theta = -lambda/a, small-angle approx.) to the first minimum on the other side (theta = +lambda/a), so its total ANGULAR width is 2.lambda/a. On a screen at distance D, the LINEAR width of the central maximum is 2.lambda.D/a -- note this is inversely proportional to the slit width a: a NARROWER slit produces a WIDER central maximum.",
    example: `Angular half-width to first minimum: theta ~ lambda / a
Total angular width of central max:   2.lambda / a
Linear width on screen (distance D):  2.lambda.D / a

Worked example: lambda=500 nm, a=0.2 mm, D=1 m
Half-width = (500x10^-9 x 1)/(0.2x10^-3) = 2.5x10^-3 m = 2.5 mm
Total central maximum width = 2 x 2.5 mm = 5 mm` },
  { section: "Diffraction", title: "Interference pattern vs. diffraction pattern — key differences",
    explanation: "Interference (as in YDSE) results from superposition of waves from TWO OR MORE distinct coherent sources, producing fringes that are ALL of essentially EQUAL width and EQUAL (comparable) intensity. Diffraction results from superposition of wavelets from DIFFERENT PARTS OF THE SAME wavefront passing a single aperture, producing a WIDE, BRIGHT central maximum with much FAINTER, narrower secondary maxima that fall off rapidly in intensity.",
    example: `                  INTERFERENCE (YDSE)      DIFFRACTION (single slit)
Origin:           2+ coherent sources      same wavefront, one slit
Fringe widths:    all equal                central band >> others
Fringe intensity: all comparable           central >> secondary (falls
                                             off rapidly with order)` },

  // ── Polarization ─────────────────────────────────────────────────────────────
  { section: "Polarization", title: "Polarization and the transverse nature of light",
    explanation: "Ordinary (unpolarized) light has its electric field vibrating randomly in ALL directions perpendicular to the direction of propagation. Polarization is the process of restricting these vibrations to just ONE plane (linear polarization). The very fact that light CAN be polarized is direct proof that light waves are TRANSVERSE (not longitudinal) -- a longitudinal wave like sound has no perpendicular directions to restrict, so it cannot be polarized at all.",
    example: `Unpolarized light: E-vector vibrates in ALL directions
                     perpendicular to propagation direction
Polarized light:   E-vector restricted to ONE fixed direction
                     (perpendicular to propagation)

Sound CANNOT be polarized (longitudinal) -- polarization is
proof that light is a TRANSVERSE wave` },
  { section: "Polarization", title: "Polaroids and polarization by selective absorption",
    explanation: "A polaroid (Polaroid sheet) contains long-chain molecules aligned in one direction that absorb the component of the electric field parallel to the chains, transmitting only the component along the PERPENDICULAR direction, called the polaroid's transmission axis. Passing unpolarized light through one ideal polaroid transmits, on average, exactly HALF the incident intensity, now linearly polarized along the transmission axis.",
    example: `Unpolarized light, intensity I0, through ONE ideal polaroid:
Transmitted intensity = I0 / 2   (now linearly polarized)

(Averaging cos^2(theta) over all random theta from 0 to 2π
gives exactly 1/2)` },
  { section: "Polarization", title: "Malus's law: I = I₀cos²θ",
    explanation: "When already-polarized light of intensity I0 (from a polarizer) is incident on a second polaroid (the ANALYZER) whose transmission axis makes angle theta with the polarization direction, only the component of the electric field amplitude along the analyzer's axis (E0.cos(theta)) is transmitted. Since intensity is proportional to amplitude squared, the transmitted intensity follows MALUS'S LAW: I = I0.cos^2(theta).",
    example: `Malus's Law:  I = I0 . cos^2(theta)

theta = 0    -> I = I0        (fully transmitted, axes aligned)
theta = 45deg -> I = I0/2      (cos^2 45 = 1/2)
theta = 90deg -> I = 0         ("crossed" polaroids, fully blocked)

Numeric: I0=100 W/m^2, theta=60deg
I = 100 x cos^2(60) = 100 x 0.25 = 25 W/m^2` },
  { section: "Polarization", title: "Combining an initial polarizer with Malus's law",
    explanation: "When UNPOLARIZED light first passes through a polarizer, its intensity halves to I0/2 (regardless of the polarizer's orientation, since the incoming light has no preferred direction). This resulting polarized light then obeys Malus's law when it meets a second polaroid (analyzer) at angle theta: final intensity = (I0/2).cos^2(theta). This two-step reasoning is essential whenever a problem starts from UNPOLARIZED light and involves two or more polaroids.",
    example: `Unpolarized I0 -> Polarizer -> I0/2 (now polarized)
              -> Analyzer at angle theta -> (I0/2).cos^2(theta)

Worked example: I0, analyzer at 30 deg to polarizer axis
Final I = (I0/2) x cos^2(30) = (I0/2) x (3/4) = 0.375 . I0` },
  { section: "Polarization", title: "Polarization by scattering",
    explanation: "When sunlight (unpolarized) is scattered by molecules of gas in the atmosphere, the scattered light observed at 90 degrees to the direction of the incident sunlight is found to be linearly polarized (nearly completely, in the ideal case). Light scattered in the forward or backward direction remains largely unpolarized, while intermediate angles show partial polarization. This is why polarizing filters/sunglasses can cut down sky glare more effectively when looking at 90 degrees to the sun.",
    example: `Sunlight (unpolarized) -> scattered by air molecules
Scattering angle 0 deg (forward)   -> unpolarized
Scattering angle 90 deg            -> (nearly) fully polarized
Scattering angle 180 deg (back)    -> unpolarized

Basis for polarized sunglasses reducing sky/haze glare` },
  { section: "Polarization", title: "Polarization by reflection and Brewster's angle",
    explanation: "When unpolarized light reflects off the surface of a transparent medium (e.g. glass or water), the reflected ray is generally partially polarized -- EXCEPT at one particular angle of incidence, called the BREWSTER ANGLE (theta_B), at which the reflected ray becomes COMPLETELY (linearly) polarized, with its E-vector perpendicular to the plane of incidence. At this angle, the reflected and refracted rays are exactly perpendicular (90 degrees) to each other.",
    example: `At Brewster's angle theta_B:
- Reflected ray: completely linearly polarized
- Angle between reflected ray and refracted ray = 90 deg
- Refracted (transmitted) ray remains partially polarized` },
  { section: "Polarization", title: "Brewster's law: n = tanθ_B",
    explanation: "Using Snell's law (n = sin(theta_B)/sin(theta_r)) together with the geometric condition that the reflected and refracted rays are perpendicular at Brewster's angle (theta_B + theta_r = 90deg, so theta_r = 90deg - theta_B), substitution gives sin(theta_r) = sin(90-theta_B) = cos(theta_B), leading directly to BREWSTER'S LAW: n = tan(theta_B), where n is the refractive index of the reflecting medium (relative to the medium of incidence).",
    example: `Snell's law: n = sin(theta_B) / sin(theta_r)
Geometry:   theta_r = 90deg - theta_B  (rays perpendicular)
=> n = sin(theta_B) / sin(90-theta_B) = sin(theta_B)/cos(theta_B)
=> n = tan(theta_B)                      <- BREWSTER'S LAW

Worked example: glass, n = 1.5
theta_B = tan^-1(1.5) ~ 56.3 degrees

Check (n=1.73): theta_B = tan^-1(1.73) = 60 degrees` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh25Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh25Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh25Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH25_QUESTIONS : NEET_PHYSICS_CH25_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 25</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Wave Optics</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 12 Physics · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH25_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH25_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
